import type { ReactNode } from 'react';
import Clock from 'svg/Clock';
import { Button, ButtonLink } from 'components/Button';
import classNames from 'styles/utils/classNames';
import { timeAgo } from './utils';

interface Props {
  request: {
    id: string;
    requestMessage?: string | null;
    createdAt: string;
  };
  /** Opens the "Cancel request?" confirmation; the card never cancels directly. */
  onCancel: () => void;
  /** Replaces the message body — the Sent availability card shows the chosen slot + court. */
  children?: ReactNode;
  /** When set, a "See request" link sits beside Cancel and opens the request thread. */
  seeRequestHref?: string;
  /** Figma's sizing from lg up: `card` keeps the card, `row` is one bare row with actions on the right. */
  desktopLayout?: 'card' | 'row';
  /** Grows the actions from lg up. Figma uses the 42px button only inside a 640px modal. */
  buttonSizeDesktop?: 'md';
}

/**
 * The viewer's live request as shown at the bottom of a game's detail modal, in
 * place of the "Request to play" button: message + time-ago + a cancel action.
 */
export default function YourRequestCard({
  request,
  onCancel,
  children,
  seeRequestHref,
  desktopLayout,
  buttonSizeDesktop,
}: Props) {
  const isRowAtDesktop = desktopLayout === 'row';

  return (
    <div
      className={classNames(
        'flex flex-col gap-ds-sm rounded-[1.25rem] bg-color-bg-lightmode-secondary p-ds-md dark:bg-color-bg-darkmode-secondary',
        desktopLayout === 'card' && 'lg:gap-ds-md lg:p-ds-lg',
        isRowAtDesktop &&
          'lg:flex-row lg:items-center lg:gap-ds-md lg:rounded-none lg:bg-transparent lg:p-0 lg:dark:bg-transparent',
      )}
    >
      <div className={classNames('flex min-w-0 flex-col gap-ds-sm', isRowAtDesktop && 'lg:flex-1')}>
        <div
          className={classNames(
            'flex items-start justify-between',
            desktopLayout && 'lg:items-center',
            isRowAtDesktop && 'lg:justify-start lg:gap-ds-lg',
          )}
        >
          <span
            className={classNames(
              'typography-product-card-title-mobile text-color-text-lightmode-primary dark:text-color-text-darkmode-primary',
              desktopLayout &&
                'lg:typography-product-card-title-desktop lg:text-color-text-lightmode-secondary lg:dark:text-color-text-darkmode-secondary',
            )}
          >
            Your request
          </span>
          <div className="flex shrink-0 items-center gap-ds-xs text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
            <Clock
              className={classNames(
                'h-4 w-4 text-color-text-lightmode-icon',
                desktopLayout === 'card' && 'lg:h-5 lg:w-5',
              )}
            />
            <span
              className={classNames(
                'typography-product-text-card-mobile',
                desktopLayout && 'lg:typography-product-text-card-desktop',
              )}
            >
              {timeAgo(request.createdAt)}
            </span>
          </div>
        </div>

        {children ??
          (request.requestMessage && (
            <p
              className={classNames(
                'typography-product-text-card-mobile truncate text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary',
                // Both desktop layouts give the message Figma's full-width bold line.
                desktopLayout &&
                  'lg:typography-product-heading-compact-mobile lg:whitespace-normal lg:py-ds-xs lg:text-color-text-lightmode-primary lg:dark:text-color-text-darkmode-primary',
              )}
            >
              {request.requestMessage}
            </p>
          ))}
      </div>

      <div className={classNames('flex gap-ds-md', isRowAtDesktop && 'lg:shrink-0')}>
        <Button
          variant="brand-secondary"
          size="sm"
          sizeDesktop={buttonSizeDesktop}
          isInlineDesktop={isRowAtDesktop}
          onClick={onCancel}
          className="whitespace-nowrap"
        >
          Cancel request
        </Button>
        {seeRequestHref && (
          <ButtonLink
            variant="secondary"
            size="sm"
            sizeDesktop={buttonSizeDesktop}
            href={seeRequestHref}
            className="whitespace-nowrap"
          >
            See request
          </ButtonLink>
        )}
      </div>
    </div>
  );
}
